// This browser's id in your account's play history (migration 0018: one row
// per device, written only by that device). Kept in its own storage key, with
// the time it was made.
//
// The one hazard is two tabs of a fresh browser each making an id at the same
// moment: the later write wins in storage, and a row written under the losing
// id would hold this device's plays a second time, counted forever. So
// nothing is written under an id until it has stood for DEVICE_SETTLE_MS:
// by then every tab reads the same one. A tab that finds an older id simply
// uses it.

const KEY = 'vlc:device';
const VALID = /^[A-Za-z0-9_-]{8,64}$/;
/** How long a new id stands before history is written under it. */
export const DEVICE_SETTLE_MS = 2000;

export interface ThisDevice {
  id: string;
  /** Safe to write under: no other tab can still replace it. */
  settled: boolean;
}

function read(): { id: string; at: number } | null {
  const raw = window.localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
    if (typeof parsed.id === 'string' && VALID.test(parsed.id) && typeof parsed.at === 'number')
      return { id: parsed.id, at: parsed.at };
  } catch {
    // An unreadable value is replaced below.
  }
  return null;
}

function mint(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `d-${random}`;
}

/** This browser's id, made on first use; null where storage is unavailable
 * (a private window that refuses it), and then nothing is written. */
export function thisDevice(now = Date.now()): ThisDevice | null {
  try {
    let device = read();
    if (!device) {
      device = { id: mint(), at: now };
      window.localStorage.setItem(KEY, JSON.stringify(device));
    }
    // A time ahead of the clock is a clock that moved, not a fresh id.
    const settled = now - device.at >= DEVICE_SETTLE_MS || device.at > now + DEVICE_SETTLE_MS;
    return { id: device.id, settled };
  } catch {
    return null;
  }
}

export const DEVICE_ID_KEY = KEY;
